/**
 * Organisation hierarchy: Global → Region → Country → Branch, then Customer → Site.
 *
 * Org nodes (global, region, country, branch) are addressed by a materialised
 * path of node codes, e.g. "global/latam/co/br-sabana". A grant on a node
 * covers that node and everything beneath it.
 *
 * A customer can have sites in several branches, so customers and sites are
 * not path segments: a grant can be narrowed to one customer or one site by
 * id. A site's resource path is its branch path; a customer's is its country.
 * Service line (MSS, EPS, CASH, FM, OTHER) is a separate dimension carried by
 * sites; a grant may optionally be limited to one service line.
 */

export const ORG_LEVELS = ['GLOBAL', 'REGION', 'COUNTRY', 'BRANCH', 'CUSTOMER', 'SITE'] as const;
export type OrgLevel = (typeof ORG_LEVELS)[number];

export const ROOT_PATH = 'global';

export const REGIONS = [
  { code: 'ukni', name: 'UK&I' },
  { code: 'latam', name: 'LATAM' },
  { code: 'europe', name: 'Europe' },
  { code: 'africa', name: 'Africa' },
  { code: 'mea', name: 'Middle East & Asia' },
] as const;

export const SERVICE_LINES = ['MSS', 'EPS', 'CASH', 'FM', 'OTHER'] as const;
export type ServiceLine = (typeof SERVICE_LINES)[number];

/** Where a role grant applies. */
export interface ScopeGrant {
  /** Materialised path of the org node the grant is attached to. */
  path: string;
  /** If set, the grant only covers this customer (within `path`). */
  customerId?: string | null;
  /** If set, the grant only covers this site (within `path`). */
  siteId?: string | null;
  /** If set, the grant only covers resources of this service line. */
  serviceLine?: ServiceLine | null;
}

/** Where a resource (survey, finding, site, customer…) lives. */
export interface ResourceScope {
  /** Org path: branch path for sites/surveys/findings, country path for customers. */
  path: string;
  customerId?: string | null;
  siteId?: string | null;
  serviceLine?: ServiceLine | null;
}

export function joinPath(...segments: string[]): string {
  for (const s of segments) {
    if (!s || s.includes('/')) throw new Error(`Invalid path segment "${s}"`);
  }
  return segments.join('/');
}

export function parentPath(path: string): string | null {
  const i = path.lastIndexOf('/');
  return i === -1 ? null : path.slice(0, i);
}

/** All ancestor paths from the root down to (and including) `path`. */
export function ancestorPaths(path: string): string[] {
  const parts = path.split('/');
  return parts.map((_, i) => parts.slice(0, i + 1).join('/'));
}

export function isWithin(path: string, ancestor: string): boolean {
  return path === ancestor || path.startsWith(ancestor + '/');
}

export function grantCovers(grant: ScopeGrant, resource: ResourceScope): boolean {
  if (!isWithin(resource.path, grant.path)) return false;
  if (grant.customerId && grant.customerId !== resource.customerId) return false;
  if (grant.siteId && grant.siteId !== resource.siteId) return false;
  if (grant.serviceLine && grant.serviceLine !== resource.serviceLine) return false;
  return true;
}

/** True when every resource `inner` covers is also covered by `outer`. */
export function grantContains(outer: ScopeGrant, inner: ScopeGrant): boolean {
  if (!isWithin(inner.path, outer.path)) return false;
  if (outer.customerId && outer.customerId !== inner.customerId) return false;
  if (outer.siteId && outer.siteId !== inner.siteId) return false;
  if (outer.serviceLine && outer.serviceLine !== inner.serviceLine) return false;
  return true;
}

export function levelOfPath(path: string): OrgLevel {
  const depth = path.split('/').length;
  const level = ORG_LEVELS[depth - 1];
  if (!level) throw new Error(`Path "${path}" is deeper than the hierarchy allows`);
  return level;
}
